// **React Imports
import { memo, useMemo, useState } from 'react'

// **MUI Imports
import { Box, Button, Divider, Skeleton, Typography } from '@mui/material'

// **Components Imports
import CustomTooltip from '../custom-tooltip'
import Icon from '../icon'

// **Utils Imports
import { FEATURE_CREDITS_TYPE, ROLES } from '../../utils/constant'
import { checkCurrentRole } from '../../utils/utils'

const QuotaTooltip = ({
  data = null,
  showWarning = true,
  showBreakdown: breakdownProp = false,
  placement = 'bottom',
  children,
  iconStyle = {},
  loading = false
}) => {
  // state
  const [showBreakdown, setShowBreakdown] = useState(breakdownProp)

  // variables
  const isAdmin = useMemo(() => checkCurrentRole(ROLES.ADMIN), [])

  const isInsufficient = useMemo(() => {
    if (data?.newCredits !== null && data?.newCredits !== undefined) {
      return data?.newCredits < 0
    }
    return false
  }, [data?.newCredits])

  const auxiliaryCredits = useMemo(() => {
    return data?.featureCredits?.find(
      (item) =>
        item?.feature === FEATURE_CREDITS_TYPE.AUXILIARY_DEVICE_PROCTORING
    )
  }, [data?.featureCredits])

  const stageCredits = useMemo(() => {
    return auxiliaryCredits?.creditsToUse > 0
      ? data?.creditsToUse - auxiliaryCredits?.creditsToUse
      : data?.creditsToUse
  }, [auxiliaryCredits, data?.creditsToUse])

  const tooltipHeader = 'Credit Summary'

  const tooltipContent = useMemo(() => {
    if (!data) return null

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          minWidth: 260
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', fontWeight: 500 }}
        >
          {data?.creditsToUse > 0
            ? `This action will deduct ${data?.creditsToUse} credit${data?.creditsToUse > 1 ? 's' : ''} from your balance.`
            : 'No credits will be deducted'}
        </Typography>

        {!showBreakdown && (
          <Typography
            variant="body2"
            sx={{
              color: 'var(--primary-main)',
              borderRadius: '6px',
              cursor: 'pointer',
              width: 'fit-content',
              padding: '4px 6px',
              fontWeight: 500,
              '&:hover': {
                color: isInsufficient
                  ? 'var(--error-main)'
                  : 'var(--primary-main)',
                textDecoration: 'underline'
              }
            }}
            onClick={() => {
              setShowBreakdown((prev) => !prev)
            }}
          >
            View more.
          </Typography>
        )}

        {showBreakdown && data?.creditsToUse > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5
            }}
          >
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Available Credits
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {data?.availableCredits ?? '-'}
              </Typography>
            </Box>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}
            >
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {auxiliaryCredits?.creditsToUse > 0
                  ? 'Stage Credits'
                  : 'Credits Required'}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: 'error.main' }}
              >
                -{stageCredits ?? '-'}
              </Typography>
            </Box>
            {auxiliaryCredits?.creditsToUse > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 4
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Auxiliary Device Credits
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: 'error.main' }}
                >
                  -{auxiliaryCredits?.creditsToUse}
                </Typography>
              </Box>
            )}
            <Divider />
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', gap: 4 }}
            >
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', fontWeight: 600 }}
              >
                Remaining Balance
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: isInsufficient ? 'error.main' : 'success.main'
                }}
              >
                {data?.newCredits ?? '-'}
              </Typography>
            </Box>
          </Box>
        )}

        {isAdmin && isInsufficient && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              mt: 0.5
            }}
          >
            {showWarning && (
              <Typography
                variant="body2"
                sx={{
                  color: 'error.main',
                  fontSize: '12px',
                  lineHeight: 1.4
                }}
              >
                You need {data?.creditsToUse - data?.availableCredits} more
                credit(s) to complete this action.
              </Typography>
            )}
            <Button
              variant="contained"
              color="primary"
              size="small"
              fullWidth
              startIcon={<Icon icon="ion:card-outline" />}
              sx={{ fontSize: '12px', py: 0.75, fontWeight: 700 }}
              onClick={() =>
                window.open('/subscription?active=credit', '_blank')
              }
            >
              Get Credits Now
            </Button>
          </Box>
        )}
      </Box>
    )
  }, [
    isAdmin,
    data,
    showBreakdown,
    isInsufficient,
    showWarning,
    auxiliaryCredits,
    stageCredits
  ])

  if (!data) return null

  if (loading) {
    return (
      <Skeleton
        animation="wave"
        height={30}
        sx={{ transform: 'scale(1,1)' }}
        width={30}
      />
    )
  }

  return (
    <CustomTooltip
      placement={placement}
      header={tooltipHeader}
      headerStyles={{
        backgroundColor: 'primary.main',
        py: 1.5,
        px: 2.5
      }}
      message={tooltipContent}
    >
      {children || (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '50%',
            '&:hover': {
              background: 'var(--action-hover)'
            }
          }}
        >
          <Icon
            icon="ph:info"
            style={{
              fontSize: '18px',
              color: isInsufficient
                ? 'var(--error-main)'
                : 'var(--text-secondary)',
              ...iconStyle
            }}
          />
        </Box>
      )}
    </CustomTooltip>
  )
}

export default memo(QuotaTooltip)
